import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatWorkflowPhase } from '../utils/workflowLabels';
import './WorkflowGatePanel.css';

const DEFAULT_DECISION_OPTIONS = ['Go', 'Conditional Go', 'Hold / Nachlieferung', 'Senior Review', 'No-Go'];

const DECISION_COPY = {
    Go: 'Freigeben',
    'Conditional Go': 'Mit Auflagen',
    'Hold / Nachlieferung': 'Klärung',
    'Senior Review': 'Eskalieren',
    'No-Go': 'Stoppen'
};

const READINESS_COPY = {
    ready: { label: 'Entscheidungsreif', tone: 'ready' },
    conditional: { label: 'Mit Restpunkten', tone: 'conditional' },
    blocked: { label: 'Muss-Kriterien offen', tone: 'blocked' }
};

const formatGateDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const resolveGateFromPayload = (payload = {}) => payload.decisionGate || payload.project?.decisionGate || null;

const toDateOnly = (dateValue = new Date()) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
};

const addDays = (dateValue, days) => {
    const date = dateValue ? new Date(dateValue) : new Date();
    date.setDate(date.getDate() + days);
    return date;
};

const buildGateTaskProposal = (gate = {}, decision = '') => {
    if (decision === 'No-Go') return null;
    if (decision === 'Conditional Go') {
        return {
            actionKey: `${gate.key}.conditions`,
            title: 'Gate-Auflagen schließen und dokumentieren',
            dueDate: toDateOnly(addDays(new Date(), 7))
        };
    }
    if (decision === 'Hold / Nachlieferung') {
        return {
            actionKey: `${gate.key}.hold`,
            title: 'Nachlieferung / Klärung steuern',
            dueDate: toDateOnly(addDays(new Date(), 7))
        };
    }
    if (decision === 'Senior Review') {
        return {
            actionKey: `${gate.key}.senior_review`,
            title: 'Senior Review durchführen',
            dueDate: toDateOnly(addDays(new Date(), 2))
        };
    }
    if (decision === 'Go' && gate.goTask?.actionKey) {
        return {
            actionKey: gate.goTask.actionKey,
            title: gate.goTask.title,
            dueDate: toDateOnly(addDays(new Date(), gate.goTask.dueInDays || 0))
        };
    }
    return null;
};

const GateProgress = ({ progress = null, projectId }) => {
    const steps = progress?.steps || [];
    if (!steps.length) return null;

    return (
        <div className="workflow-gate-progress" aria-label="Gate-Fortschritt">
            <div className="workflow-gate-progress-header">
                <span>Lebenszyklus</span>
                <strong>{progress.currentIndex + 1} / {steps.length}</strong>
            </div>
            <div className="workflow-gate-progress-track">
                {steps.map((step) => (
                    <Link
                        key={step.key}
                        to={`/project/${projectId}/${step.path || 'dashboard'}`}
                        className={`workflow-gate-progress-step workflow-gate-progress-step-${step.state || 'upcoming'} ${step.current ? 'workflow-gate-progress-step-current' : ''}`}
                        title={`${step.label}: ${step.statusLabel || 'offen'}`}
                        aria-current={step.current ? 'step' : undefined}
                    >
                        <span>{step.label}</span>
                        <strong>{step.statusLabel}</strong>
                    </Link>
                ))}
            </div>
        </div>
    );
};

const ReadinessCriteria = ({ gate, projectId }) => {
    const criteria = gate.readiness?.criteria || [];
    if (!criteria.length) return null;

    return (
        <div className="workflow-gate-criteria">
            <div className="workflow-gate-section-heading">
                <div>
                    <span>Decision Preflight</span>
                    <strong>{gate.readiness.passedCount}/{gate.readiness.totalCount} Kriterien belegt</strong>
                </div>
                <span>{gate.readiness.score}/100</span>
            </div>
            <div className="workflow-gate-criteria-grid">
                {criteria.map((criterion) => (
                    <Link
                        key={criterion.id}
                        to={`/project/${projectId}/${criterion.path || gate.path || 'dashboard'}`}
                        className={`workflow-gate-criterion workflow-gate-criterion-${criterion.status}`}
                    >
                        <span className="workflow-gate-criterion-status" aria-hidden="true">
                            {criterion.status === 'pass' ? '✓' : criterion.status === 'warning' ? '!' : '×'}
                        </span>
                        <span className="workflow-gate-criterion-copy">
                            <strong>{criterion.label}</strong>
                            <small>{criterion.value} · Quelle: {criterion.source}</small>
                        </span>
                        <span className="workflow-gate-criterion-link">Öffnen</span>
                    </Link>
                ))}
            </div>
        </div>
    );
};

const WorkflowGatePanel = ({
    projectId,
    initialGate = null,
    title = 'Decision Gate',
    compact = false,
    gatePhase = '',
    defaultExpanded = false,
    onSaved = null,
    onLoaded = null
}) => {
    const [gate, setGate] = useState(initialGate);
    const [loading, setLoading] = useState(!initialGate);
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [correctingDecision, setCorrectingDecision] = useState(false);
    const [message, setMessage] = useState('');
    const [form, setForm] = useState({
        decision: 'Go',
        responsible: '',
        rationale: '',
        conditions: '',
        riskAccepted: false,
        createTask: true,
        taskDueDate: ''
    });

    const loadGate = async ({ silent = false } = {}) => {
        if (!projectId) return null;
        if (!silent) setLoading(true);

        try {
            const response = await axios.get(`/api/airtable/customer-portal/${projectId}`, {
                params: gatePhase ? { gatePhase } : {}
            });
            const nextGate = resolveGateFromPayload(response.data);
            setGate(nextGate);
            if (onLoaded) onLoaded(response.data);
            return nextGate;
        } catch (error) {
            console.error('Decision Gate konnte nicht geladen werden:', error.response ? error.response.data : error.message);
            if (!silent) setMessage('Decision Gate konnte nicht geladen werden.');
            return null;
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (initialGate) {
            setGate(initialGate);
            setLoading(false);
            return;
        }
        loadGate();
    }, [projectId, initialGate, gatePhase]); // eslint-disable-line react-hooks/exhaustive-deps

    const recommendedDecision = gate?.decisionPolicy?.recommendedDecision || gate?.readiness?.recommendedDecision || 'Go';
    const rationaleMinimum = Number(gate?.decisionPolicy?.rationaleMinimum || 12);

    useEffect(() => {
        if (!gate) return;
        const task = buildGateTaskProposal(gate, recommendedDecision);
        setForm((current) => ({
            ...current,
            decision: recommendedDecision,
            taskDueDate: task?.dueDate || ''
        }));
    }, [gate?.key, recommendedDecision]); // eslint-disable-line react-hooks/exhaustive-deps

    const taskProposal = useMemo(() => buildGateTaskProposal(gate || {}, form.decision), [gate, form.decision]);
    const rationaleIsValid = form.rationale.trim().length >= rationaleMinimum;
    const responsibleRequired = (gate?.decisionPolicy?.responsibleRequiredFor || []).includes(form.decision) ||
        (form.decision === 'Go' && gate?.readiness?.hardBlockerCount > 0);
    const conditionsRequired = form.decision === 'Conditional Go';
    const riskAcceptanceRequired = form.decision === 'Go' && gate?.readiness?.hardBlockerCount > 0;
    const formIsValid = rationaleIsValid &&
        (!responsibleRequired || form.responsible.trim()) &&
        (!conditionsRequired || form.conditions.trim()) &&
        (!riskAcceptanceRequired || form.riskAccepted);

    const chooseDecision = (decision) => {
        const nextTask = buildGateTaskProposal(gate || {}, decision);
        setForm((current) => ({
            ...current,
            decision,
            riskAccepted: decision === 'Go' ? current.riskAccepted : false,
            taskDueDate: nextTask?.dueDate || ''
        }));
    };

    const saveGateDecision = async (event) => {
        event.preventDefault();
        if (!projectId || !formIsValid) return;

        setSaving(true);
        setMessage('');

        try {
            const response = await axios.post(`/api/workflow/project/${projectId}/gate-decision`, {
                decision: form.decision,
                responsible: form.responsible,
                rationale: form.rationale,
                conditions: form.conditions,
                riskAccepted: form.riskAccepted,
                gatePhase,
                ...(taskProposal ? {
                    taskDecision: {
                        actionKey: taskProposal.actionKey,
                        create: form.createTask,
                        dueDate: form.taskDueDate || taskProposal.dueDate
                    }
                } : {})
            });
            setForm((current) => ({ ...current, rationale: '', conditions: '', riskAccepted: false }));
            setCorrectingDecision(false);
            setExpanded(false);
            const createdTask = response.data.createdTask;
            setMessage(createdTask
                ? `Entscheidung gespeichert · Folgeaufgabe: ${createdTask.title}`
                : 'Entscheidung revisionssicher gespeichert.');
            const nextGate = await loadGate({ silent: true });
            window.dispatchEvent(new CustomEvent('foerderbuddy:project-updated', {
                detail: { projectId, source: 'workflow-gate', gate: nextGate }
            }));
            if (onSaved) onSaved({ response: response.data, gate: nextGate });
        } catch (error) {
            console.error('Gate-Entscheidung konnte nicht gespeichert werden:', error.response ? error.response.data : error.message);
            setMessage(error.response?.data?.error || 'Gate-Entscheidung konnte nicht gespeichert werden.');
        } finally {
            setSaving(false);
        }
    };

    const startDecisionCorrection = () => {
        const previousDecision = gate.latestDecision?.decision || recommendedDecision;
        const nextTask = buildGateTaskProposal(gate, previousDecision);
        setForm({
            decision: previousDecision,
            responsible: gate.latestDecision?.responsible || '',
            rationale: '',
            conditions: gate.latestDecision?.conditions || '',
            riskAccepted: false,
            createTask: true,
            taskDueDate: nextTask?.dueDate || ''
        });
        setCorrectingDecision(true);
        setExpanded(true);
        setMessage('');
    };

    if (loading) {
        return <section className="workflow-gate-panel workflow-gate-panel-loading"><p>Decision Gate wird geladen...</p></section>;
    }

    if (!gate) return null;

    const readiness = READINESS_COPY[gate.readiness?.state] || READINESS_COPY.blocked;
    const showDecisionForm = gate.decisionRequired || correctingDecision;

    return (
        <section className={`workflow-gate-panel workflow-gate-panel-${gate.status || 'open'} workflow-gate-readiness-${readiness.tone} ${compact ? 'workflow-gate-panel-compact' : ''}`}>
            <div className="workflow-gate-command-row">
                <div className="workflow-gate-command-title">
                    <p className="workflow-gate-kicker">{title} · {formatWorkflowPhase(gate.phase)}</p>
                    <h2>{gate.title}</h2>
                    <p>{gate.question}</p>
                </div>
                <div className="workflow-gate-command-metrics">
                    <div className={`workflow-gate-readiness-badge workflow-gate-readiness-badge-${readiness.tone}`}>
                        <span>{readiness.label}</span>
                        <strong>{gate.readiness?.score ?? '–'}/100</strong>
                    </div>
                    <div>
                        <span>Empfehlung</span>
                        <strong>{recommendedDecision}</strong>
                    </div>
                    <div>
                        <span>Accountable</span>
                        <strong>{gate.accountable || 'Projektleitung'}</strong>
                    </div>
                </div>
                <div className="workflow-gate-command-actions">
                    <button type="button" className="workflow-gate-toggle" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
                        {expanded ? 'Gate schließen' : showDecisionForm ? 'Entscheiden' : 'Details'}
                    </button>
                    {!gate.decisionRequired && gate.latestDecision && !correctingDecision && (
                        <button type="button" className="workflow-gate-correct" onClick={startDecisionCorrection}>Korrigieren</button>
                    )}
                </div>
            </div>

            {gate.latestDecision && (
                <div className="workflow-gate-latest-inline">
                    <strong>{gate.latestDecision.decision}</strong>
                    <span>{gate.latestDecision.responsible || 'Förderbuddy'} · {formatGateDate(gate.latestDecision.decidedAt)}</span>
                    {gate.latestDecision.conditions && <span>Auflagen: {gate.latestDecision.conditions}</span>}
                </div>
            )}

            {expanded && (
                <div className="workflow-gate-expanded">
                    <GateProgress progress={gate.progress} projectId={projectId} />
                    <ReadinessCriteria gate={gate} projectId={projectId} />

                    <div className={`workflow-gate-policy workflow-gate-policy-${readiness.tone}`}>
                        <strong>{gate.readiness?.summary || 'Entscheidungsreife prüfen.'}</strong>
                        <span>{gate.decisionPolicy?.guardrail}</span>
                    </div>

                    {showDecisionForm && (
                        <form className="workflow-gate-form" onSubmit={saveGateDecision}>
                            <fieldset>
                                <legend>Entscheidung</legend>
                                <div className="workflow-gate-decision-options">
                                    {(gate.decisionOptions || DEFAULT_DECISION_OPTIONS).map((option) => (
                                        <button
                                            type="button"
                                            key={option}
                                            className={`${form.decision === option ? 'workflow-gate-decision-selected' : ''} ${option === recommendedDecision ? 'workflow-gate-decision-recommended' : ''}`}
                                            onClick={() => chooseDecision(option)}
                                            disabled={saving}
                                            aria-pressed={form.decision === option}
                                        >
                                            <strong>{DECISION_COPY[option] || option}</strong>
                                            <small>{option === recommendedDecision ? 'Empfohlen' : option}</small>
                                        </button>
                                    ))}
                                </div>
                            </fieldset>

                            <div className="workflow-gate-form-grid">
                                <label>
                                    Verantwortlich {responsibleRequired && <span>*</span>}
                                    <input
                                        type="text"
                                        value={form.responsible}
                                        onChange={(event) => setForm((current) => ({ ...current, responsible: event.target.value }))}
                                        placeholder={gate.accountable || 'Verantwortliche Person'}
                                        disabled={saving}
                                        required={responsibleRequired}
                                    />
                                </label>
                                <label className="workflow-gate-rationale">
                                    Entscheidungsnotiz *
                                    <textarea
                                        value={form.rationale}
                                        onChange={(event) => setForm((current) => ({ ...current, rationale: event.target.value }))}
                                        placeholder="Entscheidungsgrund, zentrale Annahme und akzeptiertes Restrisiko in einem kurzen Satz."
                                        disabled={saving}
                                        required
                                    />
                                    <small>{form.rationale.trim().length}/{rationaleMinimum} Zeichen Minimum</small>
                                </label>
                                {conditionsRequired && (
                                    <label className="workflow-gate-conditions">
                                        Auflagen, Owner und Frist *
                                        <textarea
                                            value={form.conditions}
                                            onChange={(event) => setForm((current) => ({ ...current, conditions: event.target.value }))}
                                            placeholder="Welche Restpunkte müssen von wem bis wann geschlossen werden?"
                                            disabled={saving}
                                            required
                                        />
                                    </label>
                                )}
                            </div>

                            {riskAcceptanceRequired && (
                                <label className="workflow-gate-risk-acceptance">
                                    <input type="checkbox" checked={form.riskAccepted} onChange={(event) => setForm((current) => ({ ...current, riskAccepted: event.target.checked }))} />
                                    <span>Ich übernehme die Verantwortung für das Go trotz {gate.readiness.hardBlockerCount} offenem Muss-Kriterium.</span>
                                </label>
                            )}

                            {taskProposal && (
                                <div className="workflow-gate-task-inline">
                                    <label>
                                        <input type="checkbox" checked={form.createTask} onChange={(event) => setForm((current) => ({ ...current, createTask: event.target.checked }))} />
                                        <span>
                                            <strong>Folge direkt absichern</strong>
                                            <small>{taskProposal.title}</small>
                                        </span>
                                    </label>
                                    {form.createTask && (
                                        <input type="date" aria-label="Frist der Folgeaufgabe" value={form.taskDueDate || taskProposal.dueDate} onChange={(event) => setForm((current) => ({ ...current, taskDueDate: event.target.value }))} />
                                    )}
                                </div>
                            )}

                            <div className="workflow-gate-submit-row">
                                <span>{gate.decisionPolicy?.fastPath ? 'Fast Path: keine zusätzliche Freigabeschleife.' : 'Kontrollierter Pfad mit dokumentierter Verantwortung.'}</span>
                                <button type="submit" disabled={saving || !formIsValid}>
                                    {saving ? 'Speichert...' : correctingDecision ? 'Korrektur revisionssicher speichern' : `${form.decision} protokollieren`}
                                </button>
                            </div>
                        </form>
                    )}

                    {(gate.gateHistory || []).length > 0 && (
                        <details className="workflow-gate-history">
                            <summary>Entscheidungshistorie ({gate.gateHistory.length})</summary>
                            {(gate.gateHistory || []).slice(0, 20).map((entry) => (
                                <article key={entry.id || `${entry.gateKey}-${entry.decidedAt}`}>
                                    <div>
                                        <strong>{entry.decision}</strong>
                                        <span>{entry.gateTitle || entry.gateKey} · {entry.responsible || 'Förderbuddy'}</span>
                                    </div>
                                    <p>{entry.rationale}</p>
                                    {entry.conditions && <p><strong>Auflagen:</strong> {entry.conditions}</p>}
                                    {entry.decidedAt && <small>{formatGateDate(entry.decidedAt)}</small>}
                                </article>
                            ))}
                        </details>
                    )}
                </div>
            )}

            {message && <p className="workflow-gate-message" role="status">{message}</p>}
        </section>
    );
};

export default WorkflowGatePanel;
